import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCart } from '../contexts/CartContext';
import { 
  faShoppingCart, 
  faPlus, 
  faMinus, 
  faFilter, 
  faStar, 
  faLeaf, 
  faUtensils, 
  faCookieBite, 
  faCoffee, 
  faCakeCandles, 
  faWineBottle, 
  faFire, 
  faHeart,
  faCalendar,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faClock,
  faCheckCircle,
  faClipboardList,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { menuItems, menuCategories } from '../data/mock';

// Menu Item Card Component with Image Carousel
const MenuItemCard = ({ item, index, addToCart, isOrderingAvailable }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Support both single image (legacy) and multiple images
  const images = item.images || (item.image ? [item.image] : []);
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <div 
      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1 border border-gray-200"
      style={{animationDelay: `${index * 0.1}s`}}
    >
      <div className="relative overflow-hidden">
        <div className="aspect-[4/3] overflow-hidden relative">
          {/* Image Display */}
          <img 
            src={images[currentImageIndex] || "https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/placeholder.jpg"} 
            alt={`${item.name} - imagen ${currentImageIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Navigation Arrows - Solo si hay múltiples imágenes */}
          {hasMultipleImages && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100"
              >
                <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
              </button>
            </>
          )}

          {/* Image Indicators - Solo si hay múltiples imágenes */}
          {hasMultipleImages && (
            <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToImage(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    idx === currentImageIndex
                      ? 'bg-white shadow-md'
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Image Counter - Solo si hay múltiples imágenes */}
          {hasMultipleImages && (
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-black/70 text-white px-2 py-1 rounded-md text-xs font-medium">
                {currentImageIndex + 1}/{images.length}
              </span>
            </div>
          )}
        </div>
        
        {/* Badges Elegantes */}
        <div className="absolute top-3 left-3 flex flex-col space-y-1">
          {item.popular && (
            <span className="bg-black text-white px-2 py-1 rounded-md text-xs font-medium flex items-center space-x-1">
              <FontAwesomeIcon icon={faStar} className="w-3 h-3" />
              <span>Popular</span>
            </span>
          )}
          {item.vegetarian && (
            <span className="bg-black text-white px-2 py-1 rounded-md text-xs font-medium flex items-center space-x-1">
              <FontAwesomeIcon icon={faLeaf} className="w-3 h-3" />
              <span>Veggie</span>
            </span>
          )}
          {item.spicy && (
            <span className="bg-black text-white px-2 py-1 rounded-md text-xs font-medium flex items-center space-x-1">
              <FontAwesomeIcon icon={faFire} className="w-3 h-3" />
              <span>Picante</span>
            </span>
          )}
        </div>

        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span className="bg-white text-black px-3 py-1 rounded-md text-sm font-bold border border-gray-200 shadow-sm">
            ${item.price.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-sans text-xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
          {item.name}
        </h3>
        
        <p className="text-gray-600 mb-6 leading-relaxed text-sm">
          {item.description}
        </p>
        
        <div className="flex justify-center">
          <button
            onClick={() => isOrderingAvailable && addToCart(item)}
            disabled={!isOrderingAvailable}
            className={`border-2 py-3 px-6 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center space-x-2 ${
              isOrderingAvailable 
                ? 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white cursor-pointer' 
                : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
            <span>{isOrderingAvailable ? 'AGREGAR' : 'NO DISPONIBLE'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const Menu = () => {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts - aggressive approach
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Check if ordering is available (12 PM - 9 PM)
  const [isOrderingAvailable, setIsOrderingAvailable] = useState(false);
  
  useEffect(() => {
    const checkOrderingTime = () => {
      const now = new Date();
      const hours = now.getHours();
      
      // Available from 12 PM (12) to 9 PM (21)
      const available = hours >= 12 && hours < 21;
      setIsOrderingAvailable(available);
    };
    
    // Check immediately
    checkOrderingTime();
    
    // Check every minute
    const interval = setInterval(checkOrderingTime, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Use global cart context
  const { 
    cartItems, 
    setCartItems, 
    isCartOpen, 
    setIsCartOpen, 
    addToCart, 
    removeFromCart, 
    getCartTotal 
  } = useCart();
  const [filters, setFilters] = useState({
    vegetarian: false,
    popular: false,
    spicy: false
  });

  // Specialty dishes data for the new section
  const specialtyDishes = [
    {
      id: 'specialty-1',
      name: 'Ropa Vieja ADASTRA',
      description: 'Nuestra versión signature del plato más icónico de Cuba, con carne deshebrada en sofrito artesanal.',
      chefNote: 'Cocinado lentamente durante 4 horas con especias secretas de la casa.',
      price: 28,
      image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=800&h=600&fit=crop&auto=format',
      category: 'principales'
    },
    {
      id: 'specialty-2', 
      name: 'Langosta al Ajillo Cubano',
      description: 'Langosta fresca salteada con ajo, cilantro y un toque de ron añejo cubano.',
      chefNote: 'Preparada al momento con langosta de nuestros proveedores exclusivos.',
      price: 45,
      image: 'https://images.unsplash.com/photo-1559847844-5315695dadae?w=800&h=600&fit=crop&auto=format',
      category: 'principales'
    },
    {
      id: 'specialty-3',
      name: 'Flan de Coco & Guayaba',
      description: 'Postre signature que combina la cremosidad del coco con la dulzura tropical de la guayaba.',
      chefNote: 'Receta familiar guardada por tres generaciones, con guayaba importada de Cuba.',
      price: 15,
      image: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=800&h=600&fit=crop&auto=format',
      category: 'postres'
    }
  ];

  // Filter menu items
  const filteredItems = useMemo(() => {
    let items = menuItems;
    
    // Category filter
    if (selectedCategory !== 'all') {
      items = items.filter(item => item.category === selectedCategory);
    }
    
    // Additional filters
    if (filters.vegetarian) {
      items = items.filter(item => item.vegetarian);
    }
    if (filters.popular) {
      items = items.filter(item => item.popular);
    }
    if (filters.spicy) {
      items = items.filter(item => item.spicy);
    }
    
    return items;
  }, [selectedCategory, filters]);

  // Cart functions are now handled by the global context

  const toggleFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  // Scroll to filters function for CTA button
  const scrollToFilters = () => {
    const filtersSection = document.getElementById('filters-section');
    if (filtersSection) {
      filtersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get icon for category
  const getCategoryIcon = (categoryId) => {
    const iconMap = {
      'entradas': faLeaf,
      'principales': faUtensils,
      'postres': faCakeCandles,
      'bebidas': faCoffee,
      'vinos': faWineBottle,
      'all': faUtensils
    };
    return iconMap[categoryId] || faUtensils;
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section - Consistente con otras páginas */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pulpo mediterraneo 3.JPG')`
          }}
        />
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-sans text-5xl md:text-7xl font-light leading-tight mb-6">
            <span className="text-green-400 font-normal italic">Menú</span>
          </h1>
          <p className="font-sans text-xl md:text-2xl leading-relaxed opacity-90 mb-12">
            Sabores auténticos de Cuba, reinventados con técnicas contemporáneas. 
            Cada plato cuenta una historia de tradición e innovación.
          </p>
          
          {/* Statistics */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-2">
                <FontAwesomeIcon icon={faUtensils} className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                <span className="text-2xl md:text-3xl font-light text-white">40+</span>
              </div>
              <p className="text-gray-300 text-xs md:text-sm">Platos únicos</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-2">
                <FontAwesomeIcon icon={faStar} className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                <span className="text-2xl md:text-3xl font-light text-white">4.9</span>
              </div>
              <p className="text-gray-300 text-xs md:text-sm">Calificación promedio</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-2">
                <span className="text-2xl md:text-3xl font-light text-green-400">$</span>
                <span className="text-2xl md:text-3xl font-light text-white">35</span>
              </div>
              <p className="text-gray-300 text-xs md:text-sm">Precio promedio</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ordering Hours Notice */}
      {!isOrderingAvailable && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-y border-amber-200">
          <div className="container-custom max-w-4xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-center text-center md:text-left space-y-3 md:space-y-0 md:space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faClock} className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-sans text-lg md:text-xl font-semibold text-gray-900 mb-1">
                  Servicio de pedidos temporalmente no disponible
                </h3>
                <p className="font-sans text-sm md:text-base text-gray-700 leading-relaxed">
                  Nuestro horario para delivery y recogida es de <span className="font-semibold text-amber-700">12:00 PM a 9:00 PM</span>. 
                  Le invitamos a realizar su pedido dentro de este horario. ¡Gracias por su comprensión!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cómo Funciona Section */}
      <section className="bg-white py-20 relative overflow-hidden">
        <div className="container-custom max-w-6xl mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="font-sans text-4xl md:text-5xl font-light text-gray-900 mb-4">
              ¿Cómo <span className="font-normal italic text-green-600">Funciona?</span>
            </h2>
            
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Hacer tu pedido es fácil y rápido. Sigue estos simples pasos:
            </p>
          </div>

          {/* Steps with Arrows */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-4">
            {/* Step 1 */}
            <div className="text-center flex-1">
              <div className="mb-6">
                <FontAwesomeIcon icon={faUtensils} className="w-8 h-8 text-green-600 mb-4" />
              </div>
              <h4 className="font-sans text-xl font-semibold text-gray-900 mb-3">
                Agrega al Carrito
              </h4>
              <p className="text-gray-600 text-sm px-2">
                Selecciona tus platos favoritos del menú
              </p>
            </div>

            {/* Arrow */}
            <div className="text-4xl text-gray-300 hidden md:block">→</div>

            {/* Step 2 */}
            <div className="text-center flex-1">
              <div className="mb-6">
                <FontAwesomeIcon icon={faShoppingCart} className="w-8 h-8 text-green-600 mb-4" />
              </div>
              <h4 className="font-sans text-xl font-semibold text-gray-900 mb-3">
                Abrir Carrito
              </h4>
              <p className="text-gray-600 text-sm px-2">
                Abre el carrito para proceder al checkout
              </p>
            </div>

            {/* Arrow */}
            <div className="text-4xl text-gray-300 hidden md:block">→</div>

            {/* Step 3 */}
            <div className="text-center flex-1">
              <div className="mb-6">
                <FontAwesomeIcon icon={faClipboardList} className="w-8 h-8 text-green-600 mb-4" />
              </div>
              <h4 className="font-sans text-xl font-semibold text-gray-900 mb-3">
                Llena el Formulario
              </h4>
              <p className="text-gray-600 text-sm px-2">
                Completa el formulario con tu información
              </p>
            </div>

            {/* Arrow */}
            <div className="text-4xl text-gray-300 hidden md:block">→</div>

            {/* Step 4 */}
            <div className="text-center flex-1">
              <div className="mb-6">
                <FontAwesomeIcon icon={faComments} className="w-8 h-8 text-green-600 mb-4" />
              </div>
              <h4 className="font-sans text-xl font-semibold text-gray-900 mb-3">
                Te Contactamos
              </h4>
              <p className="text-gray-600 text-sm px-2">
                Te contactaremos por WhatsApp para coordinar pago y entrega
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Historia del Menú Section */}
      <section className="bg-white py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-green-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-400 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-green-500 rounded-full blur-2xl"></div>
        </div>
        
        <div className="container-custom max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch">
            
            {/* Left Side - Content */}
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 px-4 py-2 rounded-full mb-6">
                  <FontAwesomeIcon icon={faUtensils} className="w-4 h-4 text-green-600" />
                  <span className="text-green-700 font-medium text-sm">Historia del Menú</span>
                </div>
                
                <h2 className="font-sans text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  Tradición Cubana, <span className="text-green-600 font-normal italic">Innovación Moderna</span>
                </h2>
                
                <div className="w-16 h-1 bg-green-600 mb-8"></div>
              </div>
              
              <div className="space-y-6 text-gray-700">
                <p className="text-lg leading-relaxed">
                  Nuestro menú es una carta de amor a la gastronomía cubana, donde cada receta 
                  cuenta una historia que ha pasado de generación en generación, reinventada 
                  con técnicas culinarias contemporáneas.
                </p>
                
                <p className="text-lg leading-relaxed">
                  Desde los sabores auténticos de La Habana hasta las influencias 
                  internacionales que enriquecen nuestra cocina, cada plato representa 
                  un viaje sensorial que honra nuestras raíces mientras abraza la innovación.
                </p>
                
                <p className="text-lg leading-relaxed">
                  Utilizamos ingredientes frescos y locales, técnicas tradicionales 
                  perfeccionadas durante décadas, y la creatividad de nuestros chefs 
                  para ofrecerte una experiencia gastronómica única e inolvidable.
                </p>
              </div>
              
              {/* Philosophy Points */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faLeaf} className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Ingredientes Locales</h4>
                    <p className="text-gray-600 text-sm">Productos frescos de proveedores locales seleccionados</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faHeart} className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Recetas Familiares</h4>
                    <p className="text-gray-600 text-sm">Secretos culinarios transmitidos por generaciones</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Image */}
            <div className="relative h-full">
              <div className="relative bg-gray-100 rounded-3xl overflow-hidden shadow-2xl h-full">
                {/* Dulce de Frutabomba Image */}
                <div className="relative h-full min-h-[500px]">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Dulce de Frutabomba Cubano 2.JPG" 
                    alt="Dulce de Frutabomba Cubano - ADASTRA" 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Video overlay for future implementation */}
                <div className="absolute inset-0 bg-black/10 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1"></div>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-green-600/10 rounded-full"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-green-400/5 rounded-full"></div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="bg-white">
        <div className="container-custom py-20">
          
          {/* Filters Bar Elegante */}
          <div id="menu-section" className="bg-white rounded-2xl p-8 mb-12 shadow-lg border border-gray-200">
            <div className="flex flex-col lg:flex-row gap-8">
              
              {/* Category Filters */}
              <div className="flex-1">
                <h3 className="font-sans text-lg font-semibold mb-4 text-gray-800 flex items-center space-x-2">
                  <FontAwesomeIcon icon={faFilter} className="w-5 h-5 text-gray-600" />
                  <span>Categorías</span>
                </h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-medium transition-all duration-300 ${
                      selectedCategory === 'all'
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUtensils} className="w-4 h-4" />
                    <span>Todos</span>
                  </button>
                  {menuCategories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-medium transition-all duration-300 ${
                        selectedCategory === category.id
                          ? 'bg-black text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <FontAwesomeIcon icon={getCategoryIcon(category.id)} className="w-4 h-4" />
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Filters */}
              <div className="lg:w-80">
                <h3 className="font-sans text-lg font-semibold mb-4 text-gray-800 flex items-center space-x-2">
                  <FontAwesomeIcon icon={faHeart} className="w-5 h-5 text-gray-600" />
                  <span>Filtros</span>
                </h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => toggleFilter('popular')}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                      filters.popular
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faStar} className="w-4 h-4" />
                    <span>Popular</span>
                  </button>
                  <button
                    onClick={() => toggleFilter('vegetarian')}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                      filters.vegetarian
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faLeaf} className="w-4 h-4" />
                    <span>Vegetariano</span>
                  </button>
                  <button
                    onClick={() => toggleFilter('spicy')}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                      filters.spicy
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faFire} className="w-4 h-4" />
                    <span>Picante</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Grid Modernizado */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredItems.map((item, index) => (
              <MenuItemCard key={item.id} item={item} index={index} addToCart={addToCart} isOrderingAvailable={isOrderingAvailable} />
            ))}
          </div>

          {/* No items found */}
          {filteredItems.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-white/95 backdrop-blur-md rounded-3xl p-10 max-w-md mx-auto shadow-xl border border-gray-100">
                <FontAwesomeIcon icon={faUtensils} className="w-16 h-16 mx-auto text-gray-300 mb-6" />
                <h3 className="font-sans text-2xl font-bold mb-4 text-gray-800">No hay platos disponibles</h3>
                <p className="text-gray-600 mb-8">
                  No se encontraron platos con los filtros seleccionados.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setFilters({ vegetarian: false, popular: false, spicy: false });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white py-3 px-8 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-green-600/25 hover:scale-105"
                >
                  LIMPIAR FILTROS
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Nuestras Especialidades Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Nuestras <span className="text-green-600 font-normal italic">Especialidades</span>
            </h2>
            <p className="font-sans text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Los platos estrella que definen la excelencia culinaria de ADASTRA. Preparados con técnicas artesanales y los mejores ingredientes.
            </p>
          </div>

          {/* Especialidades Grid - 3 Platos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            
            {/* Carpaccio de Pulpo */}
            <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Carpaccio de pulpo.JPG" 
                alt="Carpaccio de Pulpo ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                    <h3 className="font-sans text-2xl font-medium text-white mb-2">Carpaccio de Pulpo</h3>
                    <p className="font-sans text-sm text-gray-200 mb-1">Pulpo mediterráneo con sabores auténticos</p>
                    <p className="font-sans text-xs text-gray-400">Entrante elegante</p>
                  </div>
                  <div className="text-right">
                    <span className="font-sans text-3xl font-light text-white">$5500</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cordero ADASTRA */}
            <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pescado al grill.JPG" 
                alt="Cordero ADASTRA signature dish"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                    <h3 className="font-sans text-2xl font-medium text-white mb-2">Cordero ADASTRA</h3>
                    <p className="font-sans text-sm text-gray-200 mb-1">Horneado lentamente con romero y especias</p>
                    <p className="font-sans text-xs text-gray-400">Plato signature</p>
                  </div>
                  <div className="text-right">
                    <span className="font-sans text-3xl font-light text-white">$4000</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pasta Bellahabana */}
            <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Steak Tartar.jpg" 
                alt="Pasta Bellahabana con mariscos"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                    <h3 className="font-sans text-2xl font-medium text-white mb-2">Pasta Bellahabana</h3>
                    <p className="font-sans text-sm text-gray-200 mb-1">Pasta con camarones, champiñones y aceite de trufa</p>
                    <p className="font-sans text-xs text-gray-400">Fusión moderna</p>
                  </div>
                  <div className="text-right">
                    <span className="font-sans text-3xl font-light text-white">$5900</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Menu CTA Button */}
          <div className="flex justify-center">
            <button 
              onClick={() => document.querySelector('#menu-section').scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-black bg-black text-white font-semibold px-8 py-4 rounded-lg hover:bg-white hover:text-black hover:border-black transition-colors text-lg inline-flex items-center justify-center space-x-3"
            >
              <span>VER MENÚ COMPLETO</span>
              <span>→</span>
            </button>
          </div>

        </div>
      </section>

      {/* Call to Action Final */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20de%20Patio%20grande.JPG" 
            alt="Mesa de patio del restaurante ADASTRA"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative z-10 container-custom text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-green-600/20 backdrop-blur-sm px-6 py-3 rounded-full border border-green-400/30 mb-8">
              <FontAwesomeIcon icon={faShoppingCart} className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">¿Listo para ordenar?</span>
            </div>

            <h2 className="font-sans text-4xl md:text-5xl font-light text-white mb-6">
              ¿Listo para <span className="font-normal italic text-green-400">degustar</span>?
            </h2>

            <p className="font-sans text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
              Haz tu pedido ahora y descubre por qué ADASTRA es el destino 
              gastronómico favorito de La Habana.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <button
                onClick={() => navigate('/order-checkout', { state: { cartItems } })}
                className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-full max-w-[400px] sm:w-auto sm:max-w-none"
              >
                <span>HACER PEDIDO</span>
              </button>
              
              <button
                onClick={() => navigate('/reservas')}
                className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-full max-w-[400px] sm:w-auto sm:max-w-none"
              >
                <span>RESERVAR MESA</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Cart Button Negro */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 left-6 bg-black hover:bg-gray-800 text-white p-4 rounded-full shadow-2xl hover:shadow-black/50 transition-all duration-300 hover:scale-110 z-40 border-4 border-white"
        >
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faShoppingCart} className="w-6 h-6" />
            <span className="bg-white text-black rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold animate-pulse">
              {cartItems.reduce((total, item) => total + item.quantity, 0)}
            </span>
          </div>
        </button>
      )}

      {/* El modal del carrito ahora es global y se maneja en CartModal.jsx */}
    </div>
  );
};

export default Menu;